/* eslint-disable react/forbid-prop-types */
import { TrackingContext } from '@vrbo/react-event-tracking';
import classnames from 'classnames';
import React, {
    useContext,
    useEffect,
    useState
} from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';

import { Select } from '@ferris/react/components/forms';
import { createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import CompetitorUrlComponent from '../../CompetitorUrlComponent';
import withPageFlow from '../../PageFlow/PageFlow';
import { isAddressValid } from '../../PageFlow/Predicates';
import ProgressBarLYP from '../../ProgressBarLYP';
import PDField from './PDField';
import RadioGroupComponent from './RadioGroupComponent';
import { getFullCurrencyList, getTypeOptions } from './helpers/PropertyDetailsHelper';
import { mapDispatchToProps, mapStateToProps, propertyDetailsProps } from './helpers/properties';
import {
    isPropertyNameUnicode,
    isPropertyNameValid as isPropertyNameValidFor,
    isShowingLatinPropertyName as isShowingLatinPropertyNameFor
} from './helpers/propertyNameValidation';
import DetailsSubmitButton from './DetailsSubmitButton';
import PaperContractDisclaimer from './PaperContractDisclaimer';
import { JOIN_SOURCES, JoinCtx } from '../../../contexts/join';
import { ExperimentCtx } from '../../../contexts/experiments';
import { EGTNL_TESTS_KUMO } from '../../../../server/api/abacus/constants';
import { CL_PROPERTY_INFO, AAPCL_PROPERTY_INFO } from '../../PageFlow/PageList';

export { isPropertyNameUnicode };

export function PropertyDetails(props) {
    const { trigger } = useContext(TrackingContext);

    const [currencyUpdateList, setCurrencyUpdateList] = useState([]);

    const updateCurrencyList = async () => {
        const { fetchCurrencyOnCountryCode, countryCode } = props;
        await fetchCurrencyOnCountryCode(countryCode.toUpperCase());
    };

    // this flag represent the add a property flow
    // in the CL side
    const { isAAP } = props;
    const pageNameDetailed = isAAP ? JOIN_SOURCES.AAP : JOIN_SOURCES.JOIN;

    const {
        fetchSubmarketId,
        submitPropertyOnboarding
    } = useContext(JoinCtx);

    const {
        isBucketed,
        experimentEvaluations
    } = useContext(ExperimentCtx) || {};

    const {
        [EGTNL_TESTS_KUMO.SHARED_SOURCE_AGENTIC_ONBOARDING]: agenticOnboardingValue = 1
    } = experimentEvaluations || {};

    // eslint-disable-next-line eqeqeq
    const isAgenticOnboarding = agenticOnboardingValue == 1;

    useEffect(() => {
        window.scrollTo(0, 0);
        const {
            referrer, hydrationComplete, analytics
        } = props;

        if (hydrationComplete) {
            updateCurrencyList();
        }
        trigger('pageLoad', { pageName: 'partner-qualifier: property-info', referrer });
        trigger('pageview', createPageViewEvent('Propertyinfo', pageNameDetailed));
        analytics.setAnalyticsPageName('JoinFormPropertyInfo');
        analytics.viewTTIstop();
        trigger('pageLoadGtm', { pageName: 'PropertyDetails', event: 'PropertyDetailsLoad' });
        if (isBucketed(EGTNL_TESTS_KUMO.ULX_CL_SUBMARKETID_FETCHING)) {
            fetchSubmarketId();
        }
        return () => {
            trigger('pageUnload', { referrer });
            analytics.viewTTIstart();
        };
    }, []);

    useEffect(() => {
        const { currencyList, setCurrency } = props;
        if (currencyList.values.length === 1) {
            setCurrency(currencyList.values[0]);
        }

        setCurrencyUpdateList(getFullCurrencyList(props));
    // eslint-disable-next-line react/destructuring-assignment
    }, [props.currencyList]);

    const isShowingLatinPropertyName = () => {
        const {
            locale
        } = props;
        return isShowingLatinPropertyNameFor({ locale, isAAP });
    };

    const isPropertyNameValid = () => {
        const {
            locale,
            propertyNameValidation,
            latinPropertyNameValidation,
            latinPropertyName,
            propertyName
        } = props;
        return isPropertyNameValidFor({
            locale,
            isAAP,
            propertyName,
            latinPropertyName,
            propertyNameValidation,
            latinPropertyNameValidation
        });
    };

    const isFormDataValid = () => {
        const {
            propertyTypeValidation,
            unitCountValidation,
            currencyValidation,
            websiteUrlValidation,
            location
        } = props;
        const isValidForm = (
            isPropertyNameValid()
            && isAddressValid({ addressValidation: location.addressValidation })
            && propertyTypeValidation.isValid
            && unitCountValidation.isValid
            && currencyValidation.isValid
            && ((!isAAP || !isAgenticOnboarding) || websiteUrlValidation.isValid)
        );
        return isValidForm;
    };

    const handleSubmit = async () => {
        if (isFormDataValid()) {
            const {
                propertyName, propertyType, unitCount, managerInfo, chainInfo
            } = props;
            const gaData = {
                propertyName,
                propertyType,
                propertyRooms: unitCount,
                propertyChannelManager: managerInfo,
                propertyChain: chainInfo,
                event: 'PropertyDetailsSubmit',
                id: 'PropertyDetailsSubmit'
            };

            trigger('gtmEvent', gaData);
            await submitPropertyOnboarding({
                pageName: isAAP ? AAPCL_PROPERTY_INFO : CL_PROPERTY_INFO
            });
        }
    };

    const handleManagerInfoChange = (event) => {
        const { setManagerInfo } = props;
        setManagerInfo(event.target.value);
    };

    const handleChainInfoChange = (event) => {
        const { setChainInfo } = props;
        setChainInfo(event.target.value);
    };

    const handlePropertyNameChange = (event) => {
        const { setPropertyName } = props;
        setPropertyName(event.target.value);
    };

    const handleLatinPropertyNameChange = (event) => {
        const { setLatinPropertyName } = props;
        setLatinPropertyName(event.target.value);
    };

    const handleUnitChange = (event) => {
        const { setUnitChange } = props;
        setUnitChange(event.target.value);
    };

    const handlePropertyTypeChange = (event) => {
        const { setPropertyType } = props;
        setPropertyType(event.target.value);
    };

    const handleCurrencyChange = (event) => {
        const { setCurrency } = props;
        setCurrency(event.target.value);
    };

    const handleWebsiteUrlChange = (event) => {
        const { setWebsiteUrl } = props;
        setWebsiteUrl(event.target.value);
    };

    const {
        title,
        propertyNameLabel,
        propertyNameToolTip1,
        propertyNameToolTip2,
        propertyNameToolTip3,
        propertyName,
        latinPropertyName,
        propertyTypeLabel,
        propertyType,
        currency,
        unitCount,
        roomsLabel,
        roomsToolTip,
        currencyLabel,
        managerInfoLabel,
        managerToolTip1,
        managerToolTip2,
        chainInfoLabel,
        chainInfoToolTip,
        managerInfo,
        chainInfo,
        yesLabel,
        noLabel,
        latinRomanTip
    } = props;
    const css = classnames('property_details', 'ulx-animated');

    // Combine multiline tooltips into single elements
    const propertyNameToolTip = (
        <div>
            <p>{ propertyNameToolTip1 }</p>
            <p>{ propertyNameToolTip2 }</p>
            <p>{ propertyNameToolTip3 }</p>
        </div>
    );

    const managerInfoToolTip = (
        <div>
            <p>{managerToolTip1}</p>
            <p>{managerToolTip2}</p>
        </div>
    );

    const {
        websiteUrl, websiteUrlLabel, websiteUrlValidation, websiteUrlTooltip
    } = props;

    const websiteUrlProps = {
        fieldProps: {
            id: 'website-url',
            label: websiteUrlLabel,
            type: 'text',
            value: websiteUrl,
            changeHandler: handleWebsiteUrlChange,
            error: websiteUrlValidation.error
        },
        labelProps: isAgenticOnboarding ? {
            message: <p>{websiteUrlTooltip}</p>,
            className: 'property_details__form-tooltip'
        } : null
    };

    const { latinPropertyNameValidation } = props;
    const latinPropertyNameProps = {
        fieldProps: {
            id: 'latin-property-name',
            label: propertyNameLabel,
            type: 'text',
            value: latinPropertyName,
            changeHandler: handleLatinPropertyNameChange,
            error: latinPropertyNameValidation.error
        },
        labelProps: {
            message: propertyNameToolTip,
            className: 'property_details__form-tooltip'
        }
    };
    const { propertyNameValidation } = props;
    const propertyNameProps = {
        fieldProps: {
            id: 'property-name',
            label: propertyNameLabel,
            type: 'text',
            value: propertyName,
            changeHandler: handlePropertyNameChange,
            error: propertyNameValidation.error
        },
        labelProps: {
            message: propertyNameToolTip,
            className: 'property_details__form-tooltip'
        }
    };

    const { unitCountValidation } = props;
    const roomsProps = {
        fieldProps: {
            id: 'num-of-rooms',
            label: roomsLabel,
            type: 'text',
            inputMode: 'numeric',
            pattern: '[0-9]*',
            value: unitCount,
            changeHandler: handleUnitChange,
            error: unitCountValidation.error,
            min: 1,
            max: 9999
        },
        labelProps: {
            message: <p>{roomsToolTip}</p>,
            className: 'property_details__form-tooltip'
        }
    };

    const managerInfoLabelProps = {
        message: <p>{managerInfoToolTip}</p>,
        className: 'property_details__radio-tooltip',
        precedingText: managerInfoLabel
    };

    const chainInfoLabelProps = {
        message: <p>{chainInfoToolTip}</p>,
        className: 'property_details__radio-tooltip',
        precedingText: chainInfoLabel
    };

    const worksWithManagerRadioProps = {
        labelProps: managerInfoLabelProps,
        groupName: managerInfoLabel,
        onChange: handleManagerInfoChange,
        selectedValue: managerInfo,
        yesId: 'worksWithManagerYes',
        noId: 'worksWithManagerNo',
        yesLabel,
        noLabel
    };

    const chainInfoRadioProps = {
        labelProps: chainInfoLabelProps,
        groupName: chainInfoLabel,
        onChange: handleChainInfoChange,
        selectedValue: chainInfo,
        yesId: 'chainInfoYes',
        noId: 'chainInfoNo',
        yesLabel,
        noLabel
    };

    const { currencyValidation, propertyTypeValidation } = props;

    return (
        <div className={css}>
            <ProgressBarLYP step={2} />
            <p className="property_details__title">{title}</p>
            <section className="property_details__container">
                {
                    !isShowingLatinPropertyName()
                        ? (<PDField {...latinPropertyNameProps} />)
                        : (<PDField {...propertyNameProps} />)
                }
                {
                    isPropertyNameUnicode(propertyName)
                        && isShowingLatinPropertyName()
                        && (
                            <div>
                                <div className="property_details__form_latinRomanTip">{latinRomanTip}</div>
                                <PDField {...latinPropertyNameProps} />
                            </div>
                        )
                }
                <div className="property_details__form">
                    <Select
                        classes="property_details__form"
                        fullWidth
                        name="property-type"
                        formControlProps={
                            { id: 'property-type' }
                        }
                        value={propertyType}
                        label={propertyTypeLabel}
                        onChange={handlePropertyTypeChange}
                        invalid={propertyTypeValidation?.error
                            && propertyTypeValidation?.error?.length}
                        floatLabel
                        isControlled
                        type="text"
                        validationMessage={propertyTypeValidation?.error || ''}
                        optionList={getTypeOptions(props)}
                    />
                </div>
                <PDField {...roomsProps} />
                { (isAAP || isAgenticOnboarding) && (<PDField {...websiteUrlProps} />)}
                <div className="property_details__form">
                    <Select
                        data-wdio="currency-drop-down"
                        classes="property_details__form"
                        fullWidth
                        name="currency"
                        formControlProps={
                            { id: 'currency' }
                        }
                        value={currency}
                        label={currencyLabel}
                        onChange={handleCurrencyChange}
                        invalid={currencyValidation?.error && currencyValidation?.error?.length}
                        floatLabel
                        isControlled
                        type="text"
                        validationMessage={currencyValidation?.error || ''}
                        optionList={currencyUpdateList}
                    />
                </div>
                {(!isAAP && !isAgenticOnboarding) && (
                    <>
                        <RadioGroupComponent {...worksWithManagerRadioProps} />
                        <RadioGroupComponent {...chainInfoRadioProps} />
                    </>
                )}
            </section>
            {
                !isAgenticOnboarding && (
                    <CompetitorUrlComponent />
                )
            }
            <PaperContractDisclaimer />
            <DetailsSubmitButton isFormDataValid={isFormDataValid} handleSubmit={handleSubmit} />
        </div>
    );
}

PropertyDetails.propTypes = propertyDetailsProps;

PropertyDetails.defaultProps = {
    hydrationComplete: false,
    managerInfo: 'yes',
    chainInfo: 'yes',
    // this value is set in LYP component
    isAAP: false
};

const PropertyDetailsInfo = connect(mapStateToProps, mapDispatchToProps)(PropertyDetails);
PropertyDetailsInfo.displayName = CL_PROPERTY_INFO;
export default hot(module)(withPageFlow(PropertyDetailsInfo));
